import { EmailsFilterEnum } from "features/Library/Communications/Emails/types/models";
import { FlowFiltersEnum, type FlowGoals } from "features/Library/Flows/types/models";
import { PdfFiltersEnum } from "features/Library/Pdfs/types/models";
import {
  type BaseGetOptions,
  type BaseOrderingOptions,
  type BasePagingOptions,
  type BaseParamsApi,
  type ConfigApi,
  type PriorityLevel,
} from "interfaces";
import { type WithId } from "types";
import { type FiltersMap } from "utils/filterUtils";
import {
  type AddPeopleToContentTypes,
  type AssignmentPeopleContext,
  type RolePermissions,
  type SortingDirection,
} from "../../enums";
import { type Access, type Awareness, type Enrollment, type Visibility } from "../../enums/groups";
import { type ItemsViewList } from "../Application/globaltypes/ItemsView";
import { type FiltersState } from "../Application/slices/createFiltersSlice";
import { AssessmentFiltersEnum } from "../Library/Assessments/types/state";
import { SurveyFiltersEnum } from "../Library/Surveys/types/models";
import { VideoFiltersEnum } from "../Library/Videos/types/models";
import { type ContentAssignmentState } from "./ContentAssignments/state/slices/contentAssignmentsSlice";
import { type GroupTemplateState } from "./GroupTemplate/state/reducer";
import { type EnrollmentRule } from "./Groups/AddEditGroupForms/AutomaticallyDetect/models";
import { type AddGroupState } from "./Groups/AddGroup/state/addGroupReducer";
import { type EditGroupState } from "./Groups/EditGroup/editGroupReducer";
import { type PeopleGroupsOverviewState } from "./Groups/GroupsOverview/state/groupsOverviewReducer";
import { type EnrollmentCompaniesState, type EnrollmentUsersState } from "./Groups/state/types";
import { type RolesOverviewState } from "./RolesOverview/state/rolesOverviewReducer";
import { type AddUserState } from "./Users/AddUser/state/addUserReducer";
import { type EditUserState } from "./Users/EditUser/state/editUserRootReducer";
import { type PeopleUsersOverviewState } from "./Users/UsersOverview/state/usersOverviewReducer";
import { type UserCommonState } from "./Users/state/userCommonReducer";
import { type ContentAssignmentState as ContentAssignmentStateV2 } from "./state/slices/contentAssignmentSlice";
import { type ContentAssignmentFiltersState } from "./state/slices/contentAssignmentFilterSlice";

export interface GetPeopleFlowsOptions extends BasePagingOptions {
  filters?: FiltersMap;
  searchTerm?: string;
  orderBy: string;
}

export interface GetPeopleContentOptions extends BasePagingOptions {
  filters?: FiltersMap;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: string;
  contentType: string;
}

export interface GetGroupsOptions extends BaseGetOptions {
  owners?: number[];
}

export interface GetGroupsV2Options extends BasePagingOptions, BaseOrderingOptions {
  term?: string;
  isTemplate?: boolean;
  isWithAutoEnroll?: Enrollment;
}

export interface GetGroupEnrollmentUsersPreviewRequest {
  top: number;
  rules: EnrollmentRule[];
}

export interface GetGroupsParamsApi extends BaseParamsApi {
  owners?: number[];
}

export interface GetGroupsConfigApi extends ConfigApi<GetGroupsParamsApi> {}

export interface GetGroupsUsersToAddParamsApi extends BaseParamsApi {
  groupId?: number[];
}

export interface GetGroupsUsersToAddApi extends ConfigApi<GetGroupsUsersToAddParamsApi> {}

export interface GetUsersConfigApi extends BaseParamsApi {
  accountId?: number;
}

export interface GetUsersConfigApiV2 extends GetUsersParamsV2 {
  accountId?: number;
}

export interface GetUsersParamsV2 {
  skip?: number;
  top?: number;
  sortOrder?: string;
  sortBy?: string;
  term?: string;
}

export interface GetUsersParamsApiV2 {
  params: GetUsersParamsV2;
}

export type GetUsersListAction = (
  skip: number,
  top: number,
  orderBy: string,
  filter: FiltersMap,
  search: string,
) => void;

export interface GetContentUsersParams {
  entityId: number;
  skip: number;
  top: number;
  sortingColumn: string;
  sortingDirection: SortingDirection;
  filter: FiltersMap;
  term: string;
}

export interface IManualUsersItemModel {
  country: string | null;
  createdDate: Date | string;
  department: string;
  email: string;
  firstName: string;
  id: number;
  imageUrl: string;
  lastName: string;
  title: string;
}

export interface IEnrollmentRuleModel {
  [key: string]: unknown;
}

export interface ICreatedGroupState {
  ownersIds: number[];
  name: string;
  description: string;
  imageUrl: string;
  isAware: boolean;
  isVisibleToAll: boolean | null;
  isOpen: boolean;
  isWithAutoEnroll: boolean;
  enrolmentRules: IEnrollmentRuleModel[];
  groupId: number;
  roleId: number;
}

export interface IEnrollmentUsersFilterOptionsState
  extends ItemsViewList<IEnrollmentUsersFilterModel>,
    IEnrollmentRuleModel {}

export interface IOwnerOptions {
  AccountId: number;
  Email: string;
  Id: number;
  LastName: string;
  UserId: string;
}

export interface IEnrollmentUsersFilterModel {
  country: string;
  createdDate: Date | string;
  department: string;
  email: string;
  firstName: string;
  id: number;
  imageUrl: string;
  lastName: string;
  title: string;
}

export interface IFlowAssignmentModelItem extends Omit<ContentAssignmentModelItem, "visibility"> {
  isEditable: boolean;
  published: boolean;
  hasBeenPublished: boolean;
  canBeDeleted: boolean;
  canRemove: boolean;
  canAutoStart: boolean;
  isInherited: boolean;
  priorityLevelId: number;
  dueDate: Nullable<string>;
  publisher: string;
  hidden: boolean;
  goals?: FlowGoals;
}

export interface IVideoAssignmentModelItem extends ContentAssignmentModelItem {
  canEdit: boolean;
  duration: number;
  ownerId: number;
  primaryId: number;
  type: string;
  manifests: IAssetModelManifest[];
}

export interface IEventAssignmentModelItem extends ContentAssignmentModelItem {}

export interface IPdfAssignmentModelItem extends ContentAssignmentModelItem {}

export interface IEmbeddedVideoAssignmentModelItem extends ContentAssignmentModelItem {}

export interface AssignmentModelItemBase extends ContentAssignmentModelItem {
  permissionIssuedDate: string;
}

export type PeopleContent =
  | AssignmentModelItemBase
  | IEmbeddedVideoAssignmentModelItem
  | IPdfAssignmentModelItem
  | IEventAssignmentModelItem
  | IVideoAssignmentModelItem
  | IFlowAssignmentModelItem
  | GroupScheduleResponse;

export type SelectedContentMap = Map<number, PeopleContent>;

export interface ContentResponse {
  id: number;
  title: string;
  dateCreated: string;
  dateModified: string;
  publisher: string;
  isDraft: boolean;
  hasBeenPublished: boolean;
  description: string;
  isPurchased: boolean;
  canBePublished: boolean;
  canBeDeleted: boolean;
  bag: Record<string, unknown>;
  hasAccess: boolean;
  visibility: boolean;
  thumbnailUrl?: string | null;
}

export interface GroupScheduleResponse {
  id: number;
  title: string;
  publisher: string;
  priority: number;
  startFlowDate: string;
  endFlowDate: string | null;
  dueDate: string | null;
  daysToComplete?: number;
  hasTriggers: boolean;
  hasConsumableItems: boolean;
  canAutoStart: boolean;
  isActive: boolean;
  status: number;
}

export interface ContentAssignmentModelItem {
  id: number;
  title: string;
  dateCreated: string | Date;
  dateModified: string | Date;
  expirationDate?: string;
  daysToComplete?: number;
  dueDate?: Nullable<string>;
  inherited?: boolean;
  priorityLevelId?: number;
  thumbnailUrl?: string | null;
  isInherited?: boolean;
  publisher: string;
  isDraft: boolean;
  isPurchased: boolean;
  hasAccess: boolean;
  visibility?: boolean;
}

export interface IAssetModelManifest {
  type: string;
  uri: string;
}

export interface ICreateUserModel {
  lastName: string;
  firstName: string;
  email: string;
  department?: string;
  jobTitle?: string;
  country?: string;
  password: string;
}

export interface IUserModel extends ICreateUserModel {
  id: number;
}

export interface ConfirmationBlock {
  id: number;
  name: string;
  licensesAmount: number;
  usedLicensesAmount: number;
  issuing: number;
}

export interface ConfirmationState {
  blocks: ConfirmationBlock[];
  priorityItems: PriorityItem[];
  show: boolean;
  proceed?: (value: boolean) => void;
}

export interface PriorityItem extends PriorityLevel {
  blockType: string;
  durationInSeconds?: number;
  packIds?: number[];
  hasBeenPublished?: boolean;
}

export interface FlowPriorityItem extends PriorityItem {
  autoStart: boolean;
  canAutoStart: boolean;
}

export interface UserPreviewMappingPayload {
  mapFrom: string;
  mapTo: string;
  isCustomField: boolean;
}

export interface UserPreview {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  department: string;
  country: string;
}

export interface UserInfoBase {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  department?: string;
  title?: string;
  imageUrl?: string;
  country?: string;
  officeLocation?: string;
  createDate?: string;
  jobTitle?: string;
  isBypassSso?: boolean;
  joinedDate?: string;
}

export interface UserInfo extends UserInfoBase {
  canEdit?: boolean;
}

export type UsersFilterOptions = {
  departments: { text: string; value: string }[];
  jobTitles: { text: string; value: string }[];
  officeLocations: { text: string; value: string }[] | null;
  managers: { text: string; value: string }[] | null;
  roles: { text: string; value: number }[] | null;
  licenses: { text: string; value: string }[] | null;
  defaultRoles: { text: string; value: string }[] | null;
  groups: { text: string; value: string }[] | null;
};

export interface UserInfoAction<T> {
  type: string;
  payload: T;
}

export interface EditUserInfoState {
  info?: UserInfo;
  isLoading?: boolean;
  error?: string | null;
  isAccessAuthorized?: boolean;
}

export interface IOwnerItemModel {
  canEdit: boolean;
  country: string | null;
  createDate: Date | string;
  department: string;
  email: string;
  firstName: string;
  id: number;
  imageUrl: string;
  lastName: string;
  title: string;
}

export interface ContentAssignment {
  id: number;
  title: string;
  dateCreated: string | Date;
  dateModified: string | Date;
  expirationDate?: string;
  dueDate?: string;
  inherited: boolean;
  canBeDeleted: boolean;
  priorityLevelId: number;
  contentType: string;
  publisher: string;
  publisherId: number;
  isDraft: boolean;
  ownerId: number;
  isPurchased: boolean;
  hasBeenPublished: boolean;
  canBePublished: boolean;
}

export interface IGroupOwnerOptions {
  items: IOwnerItemModel[];
  itemsCount: number;
  isLoading: boolean;
}

export interface CountryModelItem {
  id: number;
  code: string;
  name: string;
}

export interface UsersOverviewState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

export interface AddToContentModalState {
  selectedPeopleIds: number[];
  selectedContentType: AddPeopleToContentTypes;
  selectedPeopleType: AssignmentPeopleContext;
  showModal: boolean;
  priorityItems: PriorityItem[];
}

export const v2FilterMap = {
  title: "jobTitles",
  department: "departments",
  createDate: "dateCreated",
  lastLoginDate: "dateLogin",
  roleId: "roleIds",
};

export interface RoleAssignedUsers {
  roleId: number;
  userIds: number[];
}

export interface GroupInfoForm {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  enrollmentRules: EnrollmentRule[];
  isOpen: Access;
  isVisibleToAll: Visibility;
  isAware: Awareness;
  isWithAutoEnroll: Enrollment;
  membersCount: number;
  dateCreated: Date;
  dateModified: Date;
  owners: Array<{ id: number }>;
  ownerIds: number[];
  settings: GroupSettings;
  canEdit: boolean;
  canView: boolean;
  readOnly: boolean;
}

export interface GroupSettings {
  isWithAutoEnroll: boolean | null;
  isVisibleToAll: boolean | null;
  isAware: boolean | null;
  isOpen: boolean | null;
}

export interface StartUsersImportResponse {
  id: string;
  code: string;
}

const UniqueValues = {
  ...SurveyFiltersEnum,
  ...VideoFiltersEnum,
  ...FlowFiltersEnum,
  ...AssessmentFiltersEnum,
  ...PdfFiltersEnum,
  ...EmailsFilterEnum,
};
export type ContentFilters = (typeof UniqueValues)[keyof typeof UniqueValues];

export type ContentFiltersState = FiltersState<ContentFilters>;

export interface GroupUsersState {
  enrollmentRules: EnrollmentRule[];
  userIds: number[];
}

export interface SelectedItemsState {
  selectedFlows: WithId[];
  selectedVideos: WithId[];
  selectedSurveys: WithId[];
  selectedAssessments: WithId[];
  selectedEvents: WithId[];
  selectedPdfs: WithId[];
}

export type UserLineChart = {
  Date: string;
  Events: {
    AssessmentCompletes: number;
    AssessmentStarts: number;
    CumulativeAssessmentCompletes: number;
    CumulativeAssessmentStarts: number;
    CumulativeEmailClicks: number;
    CumulativeEmailOpens: number;
    CumulativeEmailSends: number;
    CumulativeFlowCompletes: number;
    CumulativeFlowStarts: number;
    CumulativePdfCompletes: number;
    CumulativeSurveyCompletes: number;
    CumulativeSurveyStarts: number;
    CumulativeVideoCompletes: number;
    CumulativeVideoStarts: number;
    CumulativeEventRegistered?: number;
    CumulativeEventAttended?: number;
    EmailClicks: number;
    EmailOpens: number;
    EmailSends: number;
    FlowCompletes: number;
    FlowStarts: number;
    PdfCompletes: number;
    SurveyCompletes: number;
    SurveyStarts: number;
    VideoCompletes: number;
    VideoStarts: number;
    EventRegistered?: number;
    EventAttended?: number;
  };
};

export type UserActivityByType = {
  AssessmentCompletes: number;
  AssessmentStarts: number;
  EmailClicks: number;
  EmailOpens: number;
  EmailSends: number;
  EmbeddedVideoCompletes: number;
  EmbeddedVideoStarts: number;
  FlowCompletes: number;
  FlowStarts: number;
  Groups: number;
  Licenses: number;
  LinkClicks: number;
  LinkImpressions: number;
  MessageClicks: number;
  PdfCompletes: number;
  SurveyCompletes: number;
  SurveyStarts: number;
  VideoCompletes: number;
  VideoStarts: number;
  PlatformInteractions: number;
  PlatformOutreach: number;
  EventRegistered: number;
  EventAttended: number;
};

// NOSONAR TODO: remove, use type from: src/features/People/state/peopleReducer.ts
export type PeopleState = {
  contentToPeople: {
    assignments: ContentAssignmentState;
    filters: ContentFiltersState;
  };
  contentAssignment: {
    content: ContentAssignmentStateV2;
    filters: ContentAssignmentFiltersState;
  };
  addGroup: AddGroupState;
  editUser: EditUserState;
  rolesOverview: RolesOverviewState;
  userCommon: UserCommonState;
  addToContentModal: AddToContentModalState;
  addUserReducer: AddUserState;
  usersOverview: PeopleUsersOverviewState;
  groupsOverview: PeopleGroupsOverviewState;
  editGroup: EditGroupState;
  groupTemplate: GroupTemplateState;
  enrollmentUsers: EnrollmentUsersState;
  enrollmentCompanies: EnrollmentCompaniesState;
  enrollmentFilterOptions: UsersFilterOptions & {
    isLoading?: boolean;
    error?: Error;
  };
};

export type GetPermissionsResponse = { formattedPermissions: RolePermissions[]; hasGlobalAccess: boolean };

export type GroupAvailableUsers = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  title: string;
  imageUrl: string | null;
  country: string;
  createDate: string;
  assignedGroupIds: number[];
};

export type UserDto = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  title: string;
  imageUrl: string | null;
  country: string;
  officeLocation: string;
  manager: string;
  canEdit: boolean;
  createDate: string;
  lastLoginDate: string | null;
  hiredDate: string | null;
  isBypassSso: boolean;
  userPrincipalName: string;
};

export interface GetOwnersResponse {
  "@odata.context": string;
  "@odata.count": number;
  value: UserDto[];
}

export enum ContentColumns {
  Title = "Title",
  Publisher = "Publisher",
  Status = "Status",
  Objective = "Objective",
  PriorityLevel = "Priority Level",
  ScheduledFor = "Scheduled For",
  Trigger = "Trigger",
}

export enum ScheduleFiltersEnum {
  DateCreated = "dateCreated",
  DateModified = "dateModified",
  Publishers = "publisherIds",
  Priorities = "priorities",
  Statuses = "statuses",
  Packs = "packIds",
  SoftwareApplications = "softwareApplications",
  Labels = "labels",
  GoalObjectives = "goalObjectiveIds",
  GoalTypes = "goalTypeIds",
}
